import { useState } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useLightMode } from '@/hooks/useLightMode'

import { DarkProvider } from '@hivewatch/dark'

import { DragEventI } from '@/components/ui'
import { CheckpointIProps } from '@/features/floor-plan'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'

import { FLOOR_PLAN_CHECKPOINTS_ANALYTICS } from '../../constants/constants'
import { DarkCheckPointList } from './DarkCheckPointList'
import { DarkEditCheckPoint } from './DarkEditCheckPoint'

export interface DarkCheckPointsPanelIProps {
  facilityId: string
  floorId: string
  checkpoints: CheckpointIProps[]
  isExpanded: boolean
  onToggleExpansion: () => void
  onTileDragStart: (event: DragEventI) => void
  onDeviceItemHoverIn: (deviceId: string) => void
  onDeviceItemHoverOut: () => void
}

/**
 * Owns the list / edit view state so collapsing the rail never loses the
 * current view. Each view renders its own DarkFloorPlanSidePanel — the same
 * shape the Tour Plans panel uses — so a view keeps its own header and footer
 * slots without lifting form state up here.
 */
export const DarkCheckPointsPanel = ({
  floorId,
  facilityId,
  checkpoints,
  isExpanded,
  onToggleExpansion,
  onTileDragStart,
  onDeviceItemHoverIn,
  onDeviceItemHoverOut,
}: DarkCheckPointsPanelIProps) => {
  const colorScheme = useLightMode()
  const me = useAppSelector(selectMe)
  const [editCheckPoint, setEditCheckPoint] = useState<CheckpointIProps>(null)
  const { setHighlightedDeviceIds } = useFloorPlan()

  const analyticsProps = (checkpointId?: string) => ({
    checkpoint_id: checkpointId,
    facility_id: facilityId,
    floor_id: floorId,
    role: me?.roleName,
  })

  const handleOnClick = (checkpoint: CheckpointIProps) => {
    setEditCheckPoint(checkpoint)
    trackEvent(
      FLOOR_PLAN_CHECKPOINTS_ANALYTICS.checkpointDetails,
      analyticsProps(checkpoint.id)
    )
  }

  const handleOnClose = () => {
    trackEvent(
      FLOOR_PLAN_CHECKPOINTS_ANALYTICS.checkpointDetailsEditCancel,
      analyticsProps(editCheckPoint?.id)
    )
    setEditCheckPoint(null)
    setHighlightedDeviceIds(null)
  }

  const handleOnDone = () => {
    trackEvent(
      FLOOR_PLAN_CHECKPOINTS_ANALYTICS.checkpointDetailsEditSave,
      analyticsProps(editCheckPoint?.id)
    )
    setEditCheckPoint(null)
    setHighlightedDeviceIds(null)
  }

  return (
    <DarkProvider>
      {editCheckPoint ? (
        <DarkEditCheckPoint
          checkpoint={editCheckPoint}
          colorScheme={colorScheme}
          isExpanded={isExpanded}
          onClose={handleOnClose}
          onDone={handleOnDone}
          onToggleExpansion={onToggleExpansion}
        />
      ) : (
        <DarkCheckPointList
          checkpoints={checkpoints}
          colorScheme={colorScheme}
          facilityId={facilityId}
          isExpanded={isExpanded}
          onCheckPointClick={handleOnClick}
          onDeviceItemHoverIn={onDeviceItemHoverIn}
          onDeviceItemHoverOut={onDeviceItemHoverOut}
          onTileDragStart={onTileDragStart}
          onToggleExpansion={onToggleExpansion}
        />
      )}
    </DarkProvider>
  )
}
